import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import 'rxjs/Rx';
import { replaceExclude } from 'app/calculators/helpers/calc.helpers';
import { IOptionCalculationResult } from './models/option-calculation-result';
import {IProbabilityCalculatorResult} from "./models/probability-calculator-result";

@Injectable()
export class CalculatorsService {

  constructor(private http: HttpClient) {}

  getRegion(stockId): Observable<number> {
    const params: HttpParams = new HttpParams()
      .append('stockId', stockId);
    return this.http.get<number>(`/optcalc/dictionary/region/stock/${stockId}`);
  }

  getFutRegion(underlyingId): Observable<number> {
    const params: HttpParams = new HttpParams()
      .append('id', underlyingId);
    return this.http.get<number>(`/futures-calculator/dictionary/region/${underlyingId}`);
  }

  getExpirations(stockId, isEod): Promise<any> {
    return this.http.get(`/optcalc/dictionary/expiration/${stockId}?isEod=${isEod}`)
      .toPromise()
      .then(response => response, () => []);
  }

  getFutureExpirations(underlyingId): Promise<any> {
    return this.http.get(`/futures-calculator/futuresExpirations?underlyingId=${underlyingId}`)
      .toPromise()
      .then(response => response, () => []);
  }

  getOptFutureExpirations(underlyingId, futExpDate, isEod): Promise<any> {
    return this.http.get(`/futures-calculator/dictionary/expirations?id=${underlyingId}&futExpDate=${futExpDate}&isEod=${isEod}`)
      .toPromise()
      .then(response => response, () => []);
  }

  getHoursMinutesToExp(longExpDate, mode, stock): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('expDate', longExpDate)
      .append('isEod', mode ? mode : false)
      .append('stock', stock);
    return this.http.get<any>('/optcalc/dictionary/calcHoursMinutes', {
      observe: 'response',
      params: params
    });
  }

  getOptionIV(stockId, expDate, strike): Observable<number> {
    const params: HttpParams = new HttpParams()
      .append('expDate', expDate)
      .append('strike', strike);
    return this.http.get<number>(`/optcalc/dictionary/iv/${stockId}`, {
      params: params
    });
  }

  getRate(stockId, days): Observable<number> {
    const params: HttpParams = new HttpParams()
      .append('days', days);
    return this.http.get<number>(`/optcalc/dictionary/interestRate/${stockId}`, {
      params: params
    });
  }


  getVolaProbability(stockId, volaType, volaTerm, mode, expDate, daysToExp): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('stockid', stockId)
      .append('expDate', expDate)
      .append('days', replaceExclude(daysToExp))
      .append('typeVola', volaType)
      .append('term', volaTerm)
      .append('isEod', mode ? mode : false);
    return this.http.get('/optcalc/probability-calculator/calcVola', {
      observe: 'response',
      params: params
    });
  }

  getFutVolaProbability(params: HttpParams): Observable<HttpResponse<any>> {
    return this.http.get('/futures-calculator/probability-calculator/calcVola', {
      observe: 'response',
      params
    });
  }

  getStockType(stockId): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('stockid', stockId);
    return this.http.get<any>(`/optcalc/dictionary/stockType/${stockId}`, {
      observe: 'response',
      params: params
    });
  }

  getOptionCalculatorParameters(stockId, mode): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('stockid', stockId)
      .append('isEod', mode ? mode : false);
    return this.http.get<any>('/optcalc/option-calculator/parameters', {
      observe: 'response',
      params: params
    });
  }

  getFutOptionCalculatorParameters(underlyingId, futExpDate): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('id', underlyingId)
      .append('futExpDate', futExpDate)
      .append('isEod', 'true');
    return this.http.get<any>('/futures-calculator/option-calculator/parameters', {
      observe: 'response',
      params: params
    });
  }

  getOptionCalculatorResult(params: HttpParams): Observable<IOptionCalculationResult> {
    return this.http.get<IOptionCalculationResult>('/optcalc/option-calculator/calculate', { params });
  }

  getFutIV(
    optionPrice: any,
    style,
    price,
    priceStrike,
    expDate,
    daysToExp,
    hoursToExp,
    minutesToExp,
    vola,
    rate,
    lastDividentDate,
    lastDividentAmount,
    dividentFrequency,
    _yield,
    underlyingId,
    mode,
    stockType,
    type
  ) {
    let params: HttpParams = new HttpParams();
    if (style) {
      params = params.append('style', style);
    }
    if (price) {
      params = params.append('price', price);
    }
    if (priceStrike !== undefined) {
      params = params.append('priceStrike', priceStrike);
    }
    if (expDate && expDate != 'Custom date') {
      const hours: String = String(hoursToExp);
      if (hours.length == 1) {
        hoursToExp = '0'.concat(hoursToExp);
      }
      const minutes: String = String(minutesToExp);
      if (minutes.length == 1) {
        minutesToExp = '0'.concat(minutesToExp);
      }
      const hoursToExpFormatter = hoursToExp ? hoursToExp : '00';
      const minutesToExpFormatter = minutesToExp ? minutesToExp : '00';
      params = params.append('expDate', `${expDate} ${hoursToExpFormatter}:${minutesToExpFormatter}`);
    }
    if (daysToExp) {
      params = params.append('daysToExp', daysToExp);
    }
    if (hoursToExp) {
      params = params.append('hoursToExp', hoursToExp);
    }
    if (minutesToExp) {
      params = params.append('minutesToExp', minutesToExp);
    }
    if (vola) {
      params = params.append('vola', vola);
    }
    if (rate != null) {
      params = params.append('rate', rate);
    }
    if (lastDividentDate) {
      params = params.append('lastDividentDate', lastDividentDate);
    }
    if (lastDividentAmount) {
      params = params.append('lastDividentAmount', lastDividentAmount);
    }
    if (dividentFrequency != null) {
      params = params.append('dividentFrequency', dividentFrequency);
    }
    if (_yield) {
      params = params.append('yield', _yield);
    }
    if (underlyingId) {
      params = params.append('id', underlyingId);
    }
    if (stockType) {
      if (stockType === 'I' || stockType === 'X') {
        params = params.append('dividendType', 'Yield');
      } else {
        if (stockType === 'S' || stockType == 'F') {
          params = params.append('dividendType', 'Regular');
        } else {
          params = params.append('dividendType', 'NoDividend');
        }
      }
    }
    if (optionPrice) {
      params = params.append('optionPrice', optionPrice ? optionPrice : false);
    }
    params = params.append('isEod', mode ? mode : false);
    if (type) {
      if (type === 'put') {
        params = params.append('optionType', 'Put');
      } else {
        params = params.append('optionType', 'Call');
      }
    }
    return this.http.get<any>('/futures-calculator/option-calculator/calcvola', {
      observe: 'response',
      params: params
    });
  }

  getIV(
    optionPrice: any,
    style,
    price,
    priceStrike,
    expDate,
    daysToExp,
    hoursToExp,
    minutesToExp,
    vola,
    rate,
    lastDividentDate,
    lastDividentAmount,
    dividentFrequency,
    _yield,
    stockId,
    mode,
    stockType,
    type
  ) {
    let params: HttpParams = new HttpParams();
    if (style) {
      params = params.append('style', style);
    }
    if (price) {
      params = params.append('price', price);
    }
    if (priceStrike) {
      params = params.append('priceStrike', priceStrike);
    }
    if (expDate && expDate != 'Custom date') {
      const hours: String = String(hoursToExp);
      if (hours.length == 1) {
        hoursToExp = '0'.concat(hoursToExp);
      }
      const minutes: String = String(minutesToExp);
      if (minutes.length == 1) {
        minutesToExp = '0'.concat(minutesToExp);
      }
      const hoursToExpFormatter = hoursToExp ? hoursToExp : '00';
      const minutesToExpFormatter = minutesToExp ? minutesToExp : '00';
      params = params.append('expDate', `${expDate} ${hoursToExpFormatter}:${minutesToExpFormatter}`);
    }
    if (daysToExp) {
      params = params.append('daysToExp', daysToExp);
    }
    if (hoursToExp) {
      params = params.append('hoursToExp', hoursToExp);
    }
    if (minutesToExp) {
      params = params.append('minutesToExp', minutesToExp);
    }
    if (vola) {
      params = params.append('vola', vola);
    }
    if (rate != null) {
      params = params.append('rate', rate);
    }
    if (lastDividentDate) {
      params = params.append('lastDividentDate', lastDividentDate);
    }
    if (lastDividentAmount) {
      params = params.append('lastDividentAmount', lastDividentAmount);
    }
    if (dividentFrequency != null) {
      params = params.append('dividentFrequency', dividentFrequency);
    }
    if (_yield) {
      params = params.append('yield', _yield);
    }
    if (stockId) {
      params = params.append('stockId', stockId);
    }
    if (stockType) {
      if (stockType === 'I' || stockType === 'X') {
        params = params.append('dividendType', 'Yield');
      } else {
        if (stockType === 'S' || stockType == 'F') {
          params = params.append('dividendType', 'Regular');
        } else {
          params = params.append('dividendType', 'NoDividend');
        }
      }
    }
    if (optionPrice) {
      params = params.append('optionPrice', optionPrice ? optionPrice : false);
    }
    params = params.append('isEod', mode ? mode : false);
    if (type) {
      if (type === 'put') {
        params = params.append('optionType', 'Put');
      } else {
        params = params.append('optionType', 'Call');
      }
    }
    return this.http.get<any>('/optcalc/option-calculator/calcvola', {
      observe: 'response',
      params: params
    });
  }

  getProbabilityCalculatorParameters(stockId, mode): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('stockid', stockId)
      .append('isEod', mode ? mode : false);
    return this.http.get<any>('/optcalc/probability-calculator/parameters', {
      observe: 'response',
      params: params
    });
  }

  getFutProbabilityCalculatorParameters(underlyingId, futExpDate): Observable<HttpResponse<any>> {
    const params: HttpParams = new HttpParams()
      .append('id', underlyingId)
      .append('futExpDate', futExpDate)
      .append('isEod', 'true');
    return this.http.get<any>('/futures-calculator/probability-calculator/parameters', {
      observe: 'response',
      params: params
    });
  }

  getProbabilityCalculatorResult(params: HttpParams): Observable<IProbabilityCalculatorResult> {
    return this.http.get<IProbabilityCalculatorResult>('/optcalc/probability-calculator/calculate', { params: params });
  }

  getFuturesCalculatorResult(params): Observable<HttpResponse<any>> {
    return this.http.get<any>('/futures-calculator/option-calculator/calculate', {observe: 'response', params});
  }

  getFutProbabilityCalculatorResult(params: HttpParams): Observable<IProbabilityCalculatorResult> {
    return this.http.get<IProbabilityCalculatorResult>('/futures-calculator/probability-calculator/calculate', { params });
  }
}
